// Screen · This week — one class's lesson for the current week (2026-09-30 redesign).
//
// Order, for a teacher: which class → the lesson's name and one plain line → the files to open
// (worksheet, Teacher Pack, required PDFs) and "Plan this lesson" → what to teach in plain words.
// Everything a teacher uses less often (the period picker, the resources for one period with
// "Use this", "Make worksheet", the session link) sits in ONE fold, "More for planning this week",
// closed by default (it remembers being opened) — and all of it keeps working inside the fold.
// The lesson is resolved by the same function Home uses (lesson-data.ts resolveClassLesson), so
// the two can never disagree. Status chips stay neutral; not-yet-planned is not an error.

import { api } from "../api";
import { navigate } from "../router";
import { weeklyQuery, weeklyHref, loadWeeklyPreparation, mountWeeklyPreparation, type WeeklyContext } from "../weekly-preparation";
import { esc } from "../dom";
import { t, type StringKey } from "../i18n";
import { icon } from "../icons";
import { gradeFromClass, termNumber } from "../curriculum";
import { pickClass, rememberClass } from "./roadmap";
import {
  resourcesFor,
  strandResourcesFor,
  splitPeriods,
  slotView,
  STRANDS,
  type NodeResources,
  type NodeStrandResources,
  type Resource,
  type PeriodSplit,
  type SlotView,
  type Strand,
} from "../materials";
import type { WeekClock } from "../clock";
import { chipHtml } from "../calendar-view";
import { resolveClassLesson, nodeWhen, noStepLine, byGrade, plainText, type ClassRow, type TermInfo } from "../lesson-data";
import { loadLessonView, filesHtml, whatToTeachHtml } from "./lesson-map";
import { pageHeader, statusChip, bestState, emptyState, type PlanState } from "../ui";
import { booksCardHtml, loadLessonRefs, teacherTitle } from "../lesson-refs";

interface CalEntry {
  entry_date: string;
  end_date: string | null;
  label: string;
  type: "teaching" | "test" | "event" | "holiday" | "training";
  is_key: boolean;
}
interface WeekClassRow extends ClassRow { periods_per_week: number }

// ── period slot (WEB-P3): which strand + ordinal is picked, remembered per browser like the
// class picker — not per node, since a teacher moving between classes naturally wants to keep
// working the same period of their own day. ──
const SLOT_STRAND_KEY = "eden:slot-strand";
const SLOT_ORDINAL_KEY = "eden:slot-ordinal";
const MORE_OPEN_KEY = "eden:week-more-open";

function rememberSlot(strand: Strand, ordinal: number): void {
  try {
    localStorage.setItem(SLOT_STRAND_KEY, strand);
    localStorage.setItem(SLOT_ORDINAL_KEY, String(ordinal));
  } catch {
    /* private browsing / storage disabled — the picker still works, just forgets on reload */
  }
}

function rememberedSlot(): { strand: Strand; ordinal: number } {
  try {
    const s = localStorage.getItem(SLOT_STRAND_KEY);
    const o = Number(localStorage.getItem(SLOT_ORDINAL_KEY));
    if (s && (STRANDS as readonly string[]).includes(s) && Number.isInteger(o) && o >= 1) {
      return { strand: s as Strand, ordinal: o };
    }
  } catch {
    /* same as above */
  }
  return { strand: "coursebook", ordinal: 1 };
}

function moreOpen(): boolean {
  try { return localStorage.getItem(MORE_OPEN_KEY) === "1"; } catch { return false; }
}

const STRAND_LABEL_KEY: Record<Strand, StringKey> = {
  coursebook: "slot.strand_coursebook",
  thread: "slot.strand_thread",
  fluency: "slot.strand_fluency",
};

/** The strand's own two tiers, uniform across all three strands even though only thread's data
 *  actually splits into (core, practice+extension) server-side. */
function strandTierArrays(strands: NodeStrandResources | null, strand: Strand): { core: Resource[]; rest: Resource[] } {
  if (!strands) return { core: [], rest: [] };
  if (strand === "thread") return { core: strands.thread.core, rest: [...strands.thread.practice, ...strands.thread.extension] };
  return { core: strands[strand], rest: [] };
}

function shortDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  const wd = t(`wd.${new Date(Date.UTC(y, m - 1, d)).getUTCDay()}` as StringKey);
  return `${wd} ${d}`;
}
/** A span (end_date set, 0019) shown as ONE range, not repeated per day. */
function shortRange(entry_date: string, end_date: string | null): string {
  return end_date && end_date > entry_date ? `${shortDate(entry_date)}–${shortDate(end_date)}` : shortDate(entry_date);
}

/** The clock's calm lines (P1b Job 3) — nothing for a plain week (5 teaching days, no test). */
function clockLinesHtml(clock: WeekClock | null, grade: number | null): string {
  if (!clock) return "";
  const plain = clock.teaching_days >= 5 && clock.tests.length === 0;
  if (plain) return "";
  const lines: string[] = [];
  if (clock.teaching_days < 5) {
    const names = clock.lost_days.map((d) => `${esc(d.label)} ${shortDate(d.date)}`).join(", ");
    lines.push(`<p class="clock-line">${icon("clock")}<span>${clock.teaching_days} teaching day${clock.teaching_days === 1 ? "" : "s"} this week${names ? ` — ${names}` : ""}</span></p>`);
  }
  for (const test of clock.tests) {
    lines.push(
      `<p class="clock-line">${icon("tests")}<a class="linkbtn" data-link href="/tests?new=test&grade=${esc(String(grade ?? ""))}">` +
        `Test ${shortDate(test.date)} · paper due ${shortDate(test.paper_due)} — draft your paper →</a></p>`,
    );
  }
  return `<div class="clock-lines">${lines.join("")}</div>`;
}

function resourceIcon(kind: Resource["kind"]): string {
  switch (kind) {
    case "audio": return icon("audio");
    case "practice": return icon("puzzle");
    case "sheet": return icon("sheet");
    case "pack": return icon("pack");
    default: return icon("book");
  }
}

function cleanRes(s: string): string {
  return s.replace(/Let_s/g, "Let's").replace(/_eden\b/g, "").replace(/\s+-\s+\d+(st|nd|rd|th) Edition/gi, "");
}

function resourceRowHtml(r: Resource, usedKey: string | null, hideRow: boolean): string {
  const isUsed = r.key === usedKey;
  const title = r.href
    ? `<a class="res-title" href="${esc(r.href)}" target="_blank" rel="noopener">${esc(cleanRes(r.title))}</a>`
    : `<span class="res-title">${esc(cleanRes(r.title))}</span>`;
  return `<div class="res-row${isUsed ? " res-used" : ""}"${hideRow ? " hidden" : ""}>
    <span class="res-icon">${resourceIcon(r.kind)}</span>
    <div class="res-body">
      ${title}
      ${r.detail ? `<span class="res-detail">${esc(cleanRes(r.detail))}</span>` : ""}
    </div>
    <button type="button" class="res-tick${isUsed ? " active" : ""}" data-resource-key="${esc(
      r.key,
    )}" aria-pressed="${isUsed}">${isUsed ? t("res.in_use") : t("res.use_this")}</button>
  </div>`;
}

/** The strand/ordinal picker (WEB-P3): week → strand → ordinal, the same cascade the plan form uses. */
function slotPickerHtml(strand: Strand, ordinal: number, split: PeriodSplit): string {
  const strandOpts = STRANDS.map(
    (s) => `<option value="${s}"${s === strand ? " selected" : ""}>${t(STRAND_LABEL_KEY[s])}</option>`,
  ).join("");
  const count = Math.max(split[strand], 1);
  const ordinalOpts = Array.from({ length: count }, (_, i) => i + 1)
    .map((n) => `<option value="${n}"${n === ordinal ? " selected" : ""}>${n}</option>`)
    .join("");
  return `<div class="slot-picker">
    <label class="inline-field"><span>Part of the week</span>
      <select id="slot-strand" aria-label="${t("plan.strand")}">${strandOpts}</select></label>
    <label class="inline-field"><span>${t("slot.period")}</span>
      <select id="slot-ordinal" aria-label="${t("slot.period")}">${ordinalOpts}</select>
      <span class="muted">${t("slot.of_n", { n: split[strand] })}</span></label>
  </div>
  <p class="muted small">${split.coursebook} coursebook periods · ${split.thread} Cambridge periods · ${split.fluency} reading periods</p>`;
}

/** Rows for one resource list, folded past `coreCount` — 0 shows everything up front. */
function resourceListHtml(rows: Resource[], coreCount: number, usedKey: string | null): string {
  if (!rows.length) return `<p class="muted">${t("mat.none")}</p>`;
  const shownCount = coreCount || rows.length;
  return (
    rows.map((r, i) => resourceRowHtml(r, usedKey, i >= shownCount && !r.required)).join("") +
    (shownCount < rows.length
      ? `<button type="button" class="res-showall linkbtn">${t("slot.browse_all", { n: rows.length })}</button>`
      : "")
  );
}

interface WorksheetContext { grade: number | null; classId: string; nodeId: string; strand: Strand; ordinal: number; termId: string; weekNo: number; lessonId?: string | null }

function activityAndWorksheetHtml(
  classroomNote: string, context: WorksheetContext, defaults: Resource[], usedKey: string | null, allRows: Resource[],
): { activity: string; worksheet: string } {
  // A deliberate "Use this" choice wins over the period's suggested cards. Book/page citations
  // have no student block payload; say so instead of pretending to import them into a worksheet.
  const chosen = usedKey ? allRows.filter((resource) => resource.key === usedKey) : defaults;
  const cards = [...new Set(chosen.filter((resource) => resource.key.startsWith("itembank:")).map((resource) => resource.key.slice(9)))];
  const query = new URLSearchParams({
    new: "worksheet", grade: String(context.grade ?? ""), class: context.classId, node: context.nodeId, term: context.termId, week: String(context.weekNo),
    title: `${context.nodeId} · ${t(STRAND_LABEL_KEY[context.strand])} ${context.ordinal}`,
  });
  if (context.lessonId) query.set("lesson", context.lessonId);
  cards.forEach((card) => query.append("card", card));
  // Owner ruling F-2026-09-17-15 (Ruling A, 2026-09-18): the cards travel, and classroom use spends
  // no M1/M2/SA1 allocation — said plainly, because the teacher has no other way to know that.
  const hint = cards.length
    ? `Starts with ${cards.length} selected item-bank card${cards.length === 1 ? "" : "s"}, ready to edit. `
      + "Classroom use does not spend a card's monthly or semester allocation."
    : "Opens a blank worksheet you can fill in.";
  return {
    activity: classroomNote ? `<p class="matline"><b>${t("slot.classroom_activity")}:</b> ${esc(plainText(cleanRes(classroomNote)))}</p>` : "",
    worksheet: context.grade
      ? `<div class="mw"><a class="btn secondary sm make-worksheet" data-link href="/tests?${esc(query.toString())}">${icon("tests")}<span>${t("slot.make_worksheet").replace(/\s*→$/, "")}</span></a><p class="hint">${esc(hint)}</p></div>`
      : "",
  };
}

/** The Resources card, filtered to the selected period slot (WEB-P3). */
function periodResourcesCard(view: SlotView, usedKey: string | null, classroomNote: string, context: WorksheetContext): string {
  const { activity, worksheet } = activityAndWorksheetHtml(classroomNote, context, view.core, usedKey, [...view.core, ...view.more]);
  return `<div class="rescard">
    <h3 class="sub-title">${t("slot.eyebrow")}</h3>
    ${activity}
    ${resourceListHtml([...view.core, ...view.more], view.core.length, usedKey)}
    ${worksheet}
  </div>`;
}

/** The week-level fallback (WEB-P3): no edges for this strand — the node's merged list stands in. */
function weekLevelFallbackCard(strand: Strand, res: NodeResources | null, usedKey: string | null, classroomNote: string, context: WorksheetContext): string {
  const rows = (res?.items ?? []).filter((r) => r.kind !== "pack" && r.kind !== "sheet");
  const { activity, worksheet } = activityAndWorksheetHtml(classroomNote, context, [], usedKey, rows);
  return `<div class="rescard">
    <h3 class="sub-title">${t("slot.eyebrow")}</h3>
    ${activity}
    <p class="hint">${t("slot.no_edges", { strand: t(STRAND_LABEL_KEY[strand]), n: rows.length })}</p>
    ${resourceListHtml(rows, 0, usedKey)}
    ${worksheet}
  </div>`;
}

function classSwitcher(classes: WeekClassRow[], picked: WeekClassRow): string {
  if (classes.length <= 1) return ""; // one class: its badge is already on the lesson card below
  if (classes.length <= 5) {
    return `<div class="seg class-tabs" role="tablist" aria-label="${t("week.class_label")}">${classes
      .map((c) => `<button type="button" class="seg-btn${c.id === picked.id ? " on" : ""}" role="tab" aria-selected="${c.id === picked.id}" data-pick-class="${esc(c.id)}">${esc(c.name)}</button>`)
      .join("")}</div>`;
  }
  return `<select id="rm-class" class="classpick" aria-label="${t("week.class_label")}">${classes
    .map((c) => `<option value="${esc(c.id)}"${c.id === picked.id ? " selected" : ""}>${esc(c.name)}</option>`)
    .join("")}</select>`;
}

export async function renderThisWeek(app: HTMLElement, _params: Record<string, string> = {}, query = new URLSearchParams(location.search)): Promise<void> {
  const [own, current] = await Promise.all([
    api<{ classes: WeekClassRow[] }>("/api/classes?scope=own"),
    api<TermInfo | null>("/api/terms/current").catch(() => null),
  ]);
  const classes = [...own.classes].sort(byGrade);
  const picked = query.has("class") ? classes.find((c) => c.id === query.get("class")) ?? null : pickClass(classes);
  if (!picked || !current) {
    app.innerHTML = `${pageHeader({ title: "Prepare this week" })}${emptyState(!picked ? "Choose one of your assigned classes to prepare its week." : "Your Lead needs to set up a school term first.", '<a class="btn secondary" data-link href="/">Back to Home</a>')}`;
    return;
  }
  rememberClass(picked.id);
  const context: WeeklyContext = { classId: picked.id, termId: query.get("term") ?? current.term.id, weekNo: query.has("week") ? Number(query.get("week")) : current.current_week ?? 1 };
  let preparation;
  try { preparation = await loadWeeklyPreparation(context); }
  catch (error) {
    app.innerHTML = `${pageHeader({ title: "Prepare this week" })}<p role="alert">This week's saved preparation could not be loaded. ${esc((error as Error).message)}</p><button class="btn" type="button" id="retry-week">Retry this week</button><a class="btn secondary" data-link href="/">Back to Home</a>`;
    app.querySelector("#retry-week")?.addEventListener("click", () => { void renderThisWeek(app, _params, query); });
    return;
  }
  const termInfo: TermInfo = { term: preparation.term, current_week: preparation.week_no, is_current: current.is_current && preparation.term.id === current.term.id };
  const grade = gradeFromClass(picked);
  const currentSelection = preparation.term.id === current.term.id && preparation.week_no === current.current_week;
  const [cal, clock] = await Promise.all([
    currentSelection ? api<{ entries: CalEntry[] }>("/api/calendar?scope=week").catch(() => ({ entries: [] })) : Promise.resolve({ entries: [] as CalEntry[] }),
    currentSelection ? api<WeekClock | { clock: null }>(`/api/clock${grade ? `?grade=${grade}` : ""}`).catch(() => null) : Promise.resolve(null),
  ]);
  const clockOk = clock && "week_no" in clock ? clock : null;
  const heading = `Prepare school week ${preparation.week_no}`;
  const eyebrow = `${picked.name} · ${preparation.term.name}`;
  const sub = "Choose the content, consult the guide, add your teaching decisions, then prepare materials.";
  const data = { week_no: preparation.week_no, lessons: preparation.lessons.filter((l) => l.can_open) };
  const classWeek = preparation.lessons.map((l) => ({ ...l, lesson_id: l.id, week_no: preparation.week_no }));
  const termN = termNumber(preparation.term);
  // The school week's lesson in the year plan (owner ruling A2, 21 Oct 2026): the same for every teacher, whatever this
  // class has planned. The selected week is explicit, so it is passed in rather than read from "now".
  const cl = await resolveClassLesson(picked, termInfo, termN ? { term: termN, week: preparation.week_no } : undefined);
  const requestedLesson = preparation.lessons.find((l) => l.id === query.get("lesson") && l.can_open);
  const requestedNode = query.get("node");
  const node = (requestedLesson?.node_id ? cl.cur?.nodes.find((n) => n.id === requestedLesson.node_id) : null)
    ?? (requestedNode ? cl.cur?.nodes.find((n) => n.id === requestedNode) : null)
    ?? cl.node;
  context.nodeId = node?.id ?? null;
  const mine = data.lessons.filter((l) => !node || l.node_id === node.id);
  const one = requestedLesson ?? (mine.length === 1 ? mine[0]! : null);
  context.lessonId = one?.id ?? null;
  // Replace, never push: reload and Back refer to the same selected class, school week and real session.
  history.replaceState(history.state, "", weeklyHref(context));
  const contextQuery = weeklyQuery(context);
  const planHref = `/plan/start?${contextQuery}`;
  const [view, refsFile] = await Promise.all([node ? loadLessonView(node.id) : Promise.resolve(null), loadLessonRefs()]);
  const ref = node ? refsFile?.nodes[node.id] ?? null : null;
  const byOthers = classWeek.filter((l) => !l.can_open);
  const state: PlanState = mine.length ? bestState(mine.map((l) => l.status)) : byOthers.length ? bestState(byOthers.map((l) => l.status)) : "none";

  // ── the period slot (WEB-P3), inside the fold ──
  const split = splitPeriods(picked.periods_per_week);
  const remembered = rememberedSlot();
  const strand = remembered.strand;
  const ordinal = Math.min(Math.max(remembered.ordinal, 1), Math.max(split[strand], 1));
  let fold = "";
  let usedKey: string | null = null;
  let resources: NodeResources | null = null;
  if (node && view) {
    resources = view.res ?? (await resourcesFor(node.id));
    const strandRes = await strandResourcesFor(node.id, node.thread);
    const { core: coreTier, rest: restTier } = strandTierArrays(strandRes, strand);
    const strandTotal = coreTier.length + restTier.length;
    const slot = slotView(coreTier, restTier, ordinal, split[strand]);
    const mat = view.mat;
    const classroomNote = mat ? (strand === "coursebook" ? mat.coursebook_note : strand === "thread" ? mat.thread_note : mat.fluency_note) : "";
    const ctx: WorksheetContext = { grade, classId: picked.id, nodeId: node.id, strand, ordinal, termId: context.termId, weekNo: context.weekNo };
    // JOB-W02 · a lesson already planned for exactly this node/strand/period: its session page is one tap away.
    const sessionForSlot = data.lessons.find((l) => l.class_id === picked.id && l.node_id === node.id && l.strand === strand && l.slot === ordinal);
    ctx.lessonId = sessionForSlot?.id ?? null;
    const sessionLink = sessionForSlot
      ? `<p class="meta"><a class="linkbtn" data-link data-session-link href="/lesson/${esc(sessionForSlot.id)}">${t("week.open_session")}</a></p>`
      : "";
    usedKey = await api<{ resource_key: string | null }>(
      `/api/materials/use?node_id=${encodeURIComponent(node.id)}&strand=${encodeURIComponent(strand)}&slot=${ordinal}`,
    ).then((r) => r.resource_key).catch(() => null); // the tick is a nicety — the list still renders without it
    fold = `<details class="card fold" id="week-more"${moreOpen() ? " open" : ""}>
      <summary>${icon("puzzle")}<span>More for planning this week</span>${icon("down", "ico-sm fold-caret")}</summary>
      <div class="fold-body">
        <h3 class="sub-title">${t("slot.eyebrow")}</h3>
        <p class="muted small">Pick a part of the week and a period to see its resources. "Use this" marks your usual pick for that period.</p>
        ${slotPickerHtml(strand, ordinal, split)}
        ${sessionLink}
        ${strandTotal > 0
          ? periodResourcesCard(slot, usedKey, classroomNote, ctx)
          : weekLevelFallbackCard(strand, resources, usedKey, classroomNote, ctx)}
      </div>
    </details>`;
  }

  // ── the hero: this week's lesson for the picked class ──
  const mapHref = node ? `/map/${encodeURIComponent(node.id)}?${contextQuery}` : "";
  const files = view ? filesHtml(view.mat, view.res) : "";
  const draft = state === "in_progress" || state === "not_started";
  const othersNote = !mine.length && byOthers.length
    ? `<p class="hero-sub muted" data-planned-by-other>Already planned this week by the class's previous teacher — only they and the Lead can open that plan.</p>`
    : "";
  const planBtn = !mine.length || !one
    ? `<a class="btn${othersNote ? " secondary" : ""}" id="plan" data-link href="${planHref}">${icon("pencil")}<span>${othersNote ? "Plan your own" : t("lesson.plan")}</span></a>`
    : `<a class="btn${draft ? "" : " secondary"}" id="plan" data-link href="${esc((draft ? `/lesson/${encodeURIComponent(one.id)}/edit` : one.plan_id ? `/plan/${encodeURIComponent(one.plan_id)}` : `/lesson/${encodeURIComponent(one.id)}`) + `?${contextQuery}`)}">${icon(draft ? "pencil" : "eye")}<span>${draft ? t("lesson.continue") : t("lesson.view_plan")}</span></a>`;
  const hero = node && view
    ? `<section class="lesson-hero card nodecard" data-node-id="${esc(node.id)}">
        <div class="lc-top"><span class="class-badge">${esc(picked.name)}</span><span class="lc-when">${esc(nodeWhen(node))}</span><span class="lc-status">${statusChip(state)}</span></div>
        <h2 class="hero-title"><a data-link href="${esc(mapHref)}">${esc(teacherTitle(node, ref))}</a></h2>
        ${view.mat?.oneliner ? `<p class="hero-sub">${esc(plainText(view.mat.oneliner))}</p>` : ""}
        ${cl.noStep && node.id === cl.node?.id ? `<p class="hero-sub muted" data-no-step>${esc(noStepLine(cl.week))}</p>` : ""}
        ${othersNote}

      </section>`
    : `<section class="lesson-hero card">
        <div class="lc-top"><span class="class-badge">${esc(picked.name)}</span><span class="lc-status">${statusChip(state)}</span></div>
        <p class="hero-sub">${grade ? t("lesson.no_map") : t("lesson.no_grade")}</p>
        ${othersNote}
        <div class="lesson-actions">${planBtn}</div>
      </section>`;

  // ── the side column: this class's plans this week + the school week ──
  // Every plan the class has this school week that the viewer can open — a catch-up lesson on another week's step too.
  const planRows = data.lessons.length
    ? data.lessons.map((l) => {
        const href = l.status === "in_progress" || l.status === "not_started"
          ? `/lesson/${esc(l.id)}/edit`
          : l.plan_id ? `/plan/${esc(l.plan_id)}` : `/lesson/${esc(l.id)}/edit`;
        return `<div class="card wk-lesson">
          <a class="wk-open" data-link href="${href}"><span>${esc(l.topic)}</span> ${statusChip(l.status)}</a>
          <a class="wk-class" data-link href="/classes/${esc(l.class_id)}">${t("nav.class_roster")} ›</a>
        </div>`;
      }).join("")
    : `<p class="muted small">${byOthers.length ? "No plan of your own this week yet — the one above is the class's previous teacher's." : t("week.empty")}</p>`;
  // Other classes' plans this week (a teacher with several classes sees them all here too).
  const others = data.lessons.filter((l) => l.class_id !== picked.id);
  const otherRows = others.map((l) => `<div class="card wk-lesson">
      <a class="wk-open" data-link href="${l.plan_id ? `/plan/${esc(l.plan_id)}` : `/lesson/${esc(l.id)}/edit`}"><span><b>${esc(l.class_name)}</b> · ${esc(l.topic)}</span> ${statusChip(l.status)}</a>
    </div>`).join("");

  const sortedEntries = [...cal.entries].sort((a, b) => Number(b.is_key) - Number(a.is_key));
  const school = `<section class="panel" aria-labelledby="ws-h">
    <h2 class="panel-title" id="ws-h">${icon("calendar")}${t("week.at_school")}</h2>
    ${sortedEntries.length
      ? `<ul class="cu-list">${sortedEntries.map((e) => `<li class="cu-row cal-row cu-${esc(e.type)}"><span class="cu-date">${esc(shortRange(e.entry_date, e.end_date))}</span><span class="cu-label">${esc(e.label)} ${chipHtml(e)}</span></li>`).join("")}</ul>`
      : `<p class="muted small">${currentSelection ? "Nothing special at school this week." : "Open the calendar for the selected term and school week."}</p>`}
    <a class="panel-link" data-link href="/calendar">${t("nav.see_full_calendar").replace(/\s*→$/, "")} ${icon("chevron", "ico-sm")}</a>
  </section>`;

  const weeklyActions = node ? `<nav class="weekly-actions" aria-label="Weekly preparation steps">
    <section class="weekly-action"><h2>1. This week's content</h2><p>Read the topic, syllabus aims, language, full word list and book pages.</p><a class="btn secondary" href="#weekly-content" data-week-content>Read this week's content</a></section>
    <section class="weekly-action"><h2>2. Teaching guide</h2><p>Consult the suggested sequence and examples for this curriculum step.</p><a class="btn secondary" data-link href="/materials-guide/${encodeURIComponent(node.id)}?${esc(contextQuery.toString())}">Open teaching guide</a></section>
    <section class="weekly-action"><h2>3. My lesson plan</h2><p>Use sourced starting points and add how this class will learn.</p>${planBtn}</section>
    <section class="weekly-action"><h2>4. Materials and worksheet</h2><p>Add slides or a video link in your plan, or choose activities to preview and print.</p><a class="btn secondary" data-link href="/materials-studio/${encodeURIComponent(node.id)}?${esc(contextQuery.toString())}">Make a worksheet for this week</a><p><a class="linkbtn" data-link href="${esc((one ? (draft ? `/lesson/${encodeURIComponent(one.id)}/edit` : one.plan_id ? `/plan/${encodeURIComponent(one.plan_id)}` : `/lesson/${encodeURIComponent(one.id)}`) + `?${contextQuery}&stage=materials` : `${planHref}&stage=materials`))}">Add slides or a video link</a></p></section>
  </nav>` : "";
  const picker = `<div class="weekly-context-picker"><label>School term<select id="weekly-term">${preparation.available_terms.map((term) => `<option value="${esc(term.id)}"${term.id === preparation.term.id ? " selected" : ""}>${esc(term.name)}${term.ay ? ` · ${esc(term.ay)}` : ""}</option>`).join("")}</select></label><label>School week<select id="weekly-week">${Array.from({ length: preparation.term.week_count }, (_, i) => `<option${i + 1 === preparation.week_no ? " selected" : ""}>${i + 1}</option>`).join("")}</select></label></div>`;
  app.innerHTML = `
    ${pageHeader({ eyebrow, title: heading, sub, actions: classSwitcher(classes, picked) })}
    ${picker}
    <section data-weekly-count></section>
    ${hero}
    ${weeklyActions}
    ${clockLinesHtml(clockOk, grade)}
    <div class="lesson-grid">
      <div class="lesson-main">
        <section id="weekly-content" class="weekly-content" tabindex="-1"><h2>This week's content</h2><p class="muted small">The curriculum step below is separate from school week ${preparation.week_no}.</p>
        ${booksCardHtml(ref)}
        ${node && view ? whatToTeachHtml(view.cur, node, view.mat, view.reg, true) : ""}</section>
        <details class="card fold"><summary>Original worksheets and teacher packs</summary><div class="fold-body">${files || `<p class="muted">${t("lesson.no_sheet")}</p>`}</div></details>
        ${fold}
      </div>
      <aside class="lesson-aside">
        <section class="panel" aria-labelledby="yp-h">
          <h2 class="panel-title" id="yp-h">${icon("plans")}Your plans this week</h2>
          ${planRows}
          ${otherRows}
        </section>
        ${school}
      </aside>
    </div>`;

  await mountWeeklyPreparation(app.querySelector<HTMLElement>("[data-weekly-count]")!, context, preparation);
  const changeWeek = (): void => {
    const termId = (app.querySelector("#weekly-term") as unknown as HTMLSelectElement).value;
    const weekNo = termId === context.termId ? Number((app.querySelector("#weekly-week") as unknown as HTMLSelectElement).value) : 1;
    navigate(weeklyHref({ classId: picked.id, termId, weekNo }));
  };
  app.querySelector("#weekly-term")?.addEventListener("change", changeWeek);
  app.querySelector("#weekly-week")?.addEventListener("change", changeWeek);
  app.querySelector("[data-week-content]")?.addEventListener("click", (event) => { event.preventDefault(); const content = app.querySelector<HTMLElement>("#weekly-content"); content?.scrollIntoView({ block: "start", behavior: "smooth" }); content?.focus({ preventScroll: true }); });
  wire(app, picked, node?.id ?? null, strand, ordinal, !!(node && resources));
}

function wire(app: HTMLElement, picked: WeekClassRow, nodeId: string | null, strand: Strand, ordinal: number, hasResources: boolean): void {
  // The class switcher (tabs, or a select for many classes): remember, then re-render.
  app.querySelectorAll<HTMLButtonElement>("[data-pick-class]").forEach((b) =>
    b.addEventListener("click", () => {
      rememberClass(b.dataset.pickClass!);
      const next = new URL(location.href); next.searchParams.set("class", b.dataset.pickClass!); next.searchParams.delete("node"); next.searchParams.delete("lesson"); history.replaceState({ ...history.state }, "", next);
      window.dispatchEvent(new Event("eden:rerender"));
    }));
  const sel = app.querySelector("#rm-class") as HTMLSelectElement | null;
  sel?.addEventListener("change", () => {
    rememberClass(sel.value);
    const next = new URL(location.href); next.searchParams.set("class", sel.value); next.searchParams.delete("node"); next.searchParams.delete("lesson"); history.replaceState({ ...history.state }, "", next);
    window.dispatchEvent(new Event("eden:rerender"));
  });

  // The fold remembers being opened (a per-viewer convenience) so a picker change keeps it open.
  const fold = app.querySelector<HTMLDetailsElement>("#week-more");
  fold?.addEventListener("toggle", () => {
    try { localStorage.setItem(MORE_OPEN_KEY, fold.open ? "1" : "0"); } catch { /* fine */ }
  });

  // "Show all N" (WEB-P2R Fix 1, extended WEB-P3): the extra rows are already in the DOM, just hidden.
  app.querySelector<HTMLButtonElement>(".res-showall")?.addEventListener("click", (ev) => {
    app.querySelectorAll<HTMLElement>(".rescard .res-row[hidden]").forEach((row) => { row.hidden = false; });
    (ev.currentTarget as HTMLButtonElement).remove();
  });

  // The period-slot picker (WEB-P3): strand and ordinal are remembered per browser, then a re-render.
  const strandSel = app.querySelector("#slot-strand") as HTMLSelectElement | null;
  strandSel?.addEventListener("change", () => {
    const v = strandSel.value;
    if ((STRANDS as readonly string[]).includes(v)) rememberSlot(v as Strand, 1); // a fresh strand starts at period 1
    window.dispatchEvent(new Event("eden:rerender"));
  });
  const ordinalSel = app.querySelector("#slot-ordinal") as HTMLSelectElement | null;
  ordinalSel?.addEventListener("change", () => {
    rememberSlot(strand, Number(ordinalSel.value) || 1);
    window.dispatchEvent(new Event("eden:rerender"));
  });

  // The "use this" tick (WEB-P2, re-keyed to the slot by WEB-P3): clicking the ticked resource again un-ticks it.
  if (nodeId && hasResources) {
    app.querySelectorAll<HTMLButtonElement>(".res-tick").forEach((btn) => {
      btn.addEventListener("click", async () => {
        const key = btn.dataset.resourceKey;
        if (!key) return;
        const already = btn.getAttribute("aria-pressed") === "true";
        btn.disabled = true;
        try {
          await api("/api/materials/use", {
            method: "PUT",
            body: JSON.stringify({ node_id: nodeId, strand, slot: ordinal, resource_key: already ? null : key }),
          });
          window.dispatchEvent(new Event("eden:rerender"));
        } catch {
          btn.disabled = false;
        }
      });
    });
  }
  void picked;
}
